/**
 * Developer dashboard layout — tab navigation for API management views.
 *
 * Provides sub-navigation (Users, Teammates, Tasks, Runs, Logs) and renders
 * the active tab via <Outlet />.
 */

import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_dashboard/developer")({
	component: DeveloperLayout,
});

const TABS = [
	{ label: "Users", href: "/developer" },
	{ label: "Teammates", href: "/developer/teammates" },
	{ label: "Tasks", href: "/developer/tasks" },
	{ label: "Runs", href: "/developer/runs" },
	{ label: "Logs", href: "/developer/logs" },
] as const;

function DeveloperLayout() {
	const routerState = useRouterState();
	const currentPath = routerState.location.pathname;

	return (
		<div className="flex flex-1 flex-col gap-6 p-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Developer</h1>
					<p className="text-sm text-muted-foreground">API management & monitoring</p>
				</div>
				<Button variant="outline" size="sm" asChild>
					<a href="/docs" target="_blank" rel="noopener noreferrer">
						<BookOpen className="mr-1.5 size-4" />
						Docs
					</a>
				</Button>
			</div>
			<nav className="flex gap-1 border-b">
				{TABS.map((tab) => {
					const isActive =
						tab.href === "/developer"
							? currentPath === "/developer" || currentPath === "/developer/"
							: currentPath.startsWith(tab.href);
					return (
						<Link
							key={tab.href}
							to={tab.href}
							className={cn(
								"border-b-2 px-4 py-2 text-sm font-medium transition-colors",
								isActive
									? "border-primary text-primary"
									: "border-transparent text-muted-foreground hover:text-foreground"
							)}
						>
							{tab.label}
						</Link>
					);
				})}
			</nav>
			<Outlet />
		</div>
	);
}
